import React, { useMemo } from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import CountdownTimer from './countdown-timer';
import styled from 'styled-components';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { IntermediateResult, passesQualityCheck, QualityResults } from '../../utils/network';
import colors from '../../utils/colors';

const Title = styled.h2`
	-webkit-text-fill-color: transparent;
	margin-top: 24px;
`;

const Description = styled.h6`
	text-align: center;
	color: white;
	margin: 8px 0 24px 0;
`;

const GraphContainer = styled.div`
	width: 656px;
	height: fit-content;
	background-color: rgba(255, 255, 255, 0.03);
	border-radius: 12px;
	padding: 24px;
	margin-bottom: 24px;
`;

const GraphHeader = styled.div`
	font-size: 16px;
	font-weight: 700;
	line-height: 20px;
	text-align: left;
	color: white;
`;

const VideoQuality = styled.div`
	width: fit-content;
	margin: auto;
`;

const VideoQualityLabel = styled.span`
	color: #908CA0;
	margin-right: 4px;
`;

const PacketLossLabel = styled.span`
	color: #BFBBCF;
	margin-right: 40px;
`;

const BitrateLabel = styled.span`
	color: #BFBBCF;
`;

interface Props {
	startTime: number;
	intermediateResults: IntermediateResult[] | null;
	qualityResults: QualityResults | null;
	next: () => void;
	nextButtonDisabled: boolean;
}

const graphTheme = createTheme({
	palette: {
		mode: 'dark',
	},
 });

export default function ConnectivityCheck(props: Props) {

	const {
		startTime,
		intermediateResults,
		qualityResults,
		next,
		nextButtonDisabled
	} = props;

	const data = useMemo(() => {
		if (!intermediateResults) return [];
		return intermediateResults.map((r) => r.videoKbps);
	}, [intermediateResults]);

	const title = useMemo(() => {
		if (!qualityResults) return 'Connectivity check';
		if (passesQualityCheck(qualityResults)) {
			return 'Connectivity check successful';
		}
		return 'Connectivity issues';
	}, [qualityResults]);

	const description = useMemo(() => {
		if (!qualityResults) return 'We’re verifying that your connection is working as expected';
		if (passesQualityCheck(qualityResults)) {
			return 'Everything looks great. Break a leg!';
		}
		return 'There are some things to fix before you present';
	}, [qualityResults]);

	const graphXAxis = useMemo(() => {
		if (!qualityResults) {
			const xAxisLength = Math.max(30, intermediateResults ? intermediateResults.length : 0)
			return Array.from({ length: xAxisLength }, (_value, index) => index + 1);
		}
		return intermediateResults?.map((_r, i) => i + 1);
	}, [intermediateResults, qualityResults]);

	const packLossPct = useMemo(() => {
		if (!qualityResults) return '0%';
		return qualityResults.packetLossRatio.toFixed(0) + '%';
	}, [qualityResults]);

	const bitrate = useMemo(() => {
		if (!qualityResults) return '0 Mbps';
		return (qualityResults.bitrate / 1000000).toFixed(1) + ' Mbps';
	}, [qualityResults]);

	const titleStyle = useMemo((): React.CSSProperties => {
		if (!qualityResults)
			return { background: `linear-gradient(109deg, ${colors.pinkLemonadePink} -4.67%, ${colors.lemon} 69.07%, ${colors.paleGreen} 120.95%) text` };
		else if (passesQualityCheck(qualityResults))
			return { background: 'linear-gradient(90deg, #5AE4FF 0%, #FFFD62 100%) text' };
		else
			return { background: 'linear-gradient(90deg, #FF6262 0%, #D317B4 100%) text' };

	}, [qualityResults]);

	const showNextButton = useMemo(() => {
		return qualityResults != null && passesQualityCheck(qualityResults);
	}, [qualityResults]);

	return (
		<div className="panel teal-green">
			{(intermediateResults && intermediateResults.length > 0 && !qualityResults) && (
				<CountdownTimer seconds={30} startTime={startTime} />
			)}
			<Title className="bl-font-header" style={titleStyle}>{title}</Title>
			<Description className="bl-font-header">{description}</Description>
			<GraphContainer className='onboarding-graph-container'>
				<GraphHeader>Video Bitrate Stability</GraphHeader>
				<ThemeProvider theme={graphTheme}>
					<LineChart
						loading={!intermediateResults || intermediateResults.length === 0}
						grid={{ vertical: false, horizontal: true }}
						xAxis={[{
							data: graphXAxis,
							valueFormatter: (value) => value.toString() + 's',
						}]}
						yAxis={[{
							data: [1000, 2000, 3000, 4000],
							min: 0,
							valueFormatter: (value) => {
								return value > 0 ? (value / 1000).toFixed(1) + ' Mb/s' : ''
							},
						}]}
						series={[{
							data: data,
							color: colors.paleGreen,
							valueFormatter: (value) => value ? (value / 1000).toFixed(1) + ' Mb/s' : ''
						}]}
						width={622}
						height={282}
						slotProps={{
                            legend: { hidden: true },
                            loadingOverlay: { message: !qualityResults ? 'Starting network test...' : "No data to display" },
                        }}
						sx={{
							[`& .${axisClasses.root} .${axisClasses.tickLabel}`]: {
								fill: 'rgba(255, 255, 255, 0.4)',
							},
							[`& .${axisClasses.root} .${axisClasses.tick}`]: {
								strokeWidth: 0,
							},
							[`& .${axisClasses.root} .${axisClasses.line}`]: {
								strokeWidth: 0,
							},
							padding: '8px',
						}}
						/>
				</ThemeProvider>
				{qualityResults && (
					<VideoQuality>
						<VideoQualityLabel className="bl-font-p2">Avg. packet loss:</VideoQualityLabel>
						<PacketLossLabel className="bl-font-l2">{packLossPct}</PacketLossLabel>
						<VideoQualityLabel className="bl-font-p2">Avg. video bitrate:</VideoQualityLabel>
						<BitrateLabel className="bl-font-l2">{bitrate}</BitrateLabel>
					</VideoQuality>
				)}
			</GraphContainer>
			{showNextButton &&
				<div className='button-container'>
					<button
						disabled={nextButtonDisabled}
						className='next-bttn-onboarding sherbert'
						style={{marginLeft: 0}}
						onClick={next}>
							Continue
					</button>
				</div>
			}
		</div>
	);
}
